import { useEffect, useState } from "react";
import { t } from "@/lib/i18n";
import { WifiOff, Gamepad2, Brain, Lightbulb, RefreshCw } from "lucide-react";
import SortGame from "@/components/offline/SortGame";
import ClimateQuiz from "@/components/offline/ClimateQuiz";
import ActionTips from "@/components/offline/ActionTips";

const TABS = [
  { key: "game", label: "Sort the Waste", icon: Gamepad2 },
  { key: "quiz", label: "Climate Quiz", icon: Brain },
  { key: "tips", label: "Small Actions", icon: Lightbulb },
];

export default function OfflineOverlay() {
  const [offline, setOffline] = useState(typeof navigator !== "undefined" ? !navigator.onLine : false);
  const [back, setBack] = useState(false);
  const [tab, setTab] = useState("game");

  useEffect(() => {
    const off = () => { setOffline(true); setBack(false); };
    const on = () => { setBack(true); setTimeout(() => { setOffline(false); setBack(false); }, 1200); };
    window.addEventListener("offline", off);
    window.addEventListener("online", on);
    return () => { window.removeEventListener("offline", off); window.removeEventListener("online", on); };
  }, []);

  if (!offline) return null;
  return (
    <div className="fixed inset-0 z-[90] bg-[var(--paper)] jali-bg overflow-y-auto" data-testid="offline-overlay" role="dialog" aria-label="You are offline">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="flex items-center gap-3">
          <img src="/icon-192.png" alt="" className="w-11 h-11 rounded-full" />
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold ${back ? "bg-[var(--leaf-soft)] text-[var(--leaf-dark)]" : "bg-[var(--marigold-soft)] text-[#7A4B07]"}`} data-testid="offline-status">
            {back ? <RefreshCw size={13} className="animate-spin" /> : <WifiOff size={13} />}
            {back ? t("Back online — reconnecting…") : t("You're offline · we'll reconnect automatically")}
          </div>
        </div>
        <h1 className="mt-6 text-3xl sm:text-4xl font-bold leading-tight">
          {t("The internet took a break.")}<br /><span className="text-[var(--leaf)]">{t("The planet doesn't have to.")}</span>
        </h1>
        <p className="mt-3 text-sm sm:text-base text-[var(--ink-soft)]">{t("Play a quick round while we wait — every answer is something you can actually do at home.")}</p>

        <div className="mt-6 grid grid-cols-3 gap-2 p-1.5 bg-white rounded-2xl border border-[rgba(20,40,26,.08)]" role="tablist">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button key={key} onClick={() => setTab(key)} role="tab" aria-selected={tab === key} data-testid={`offline-tab-${key}`}
              className={`flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs sm:text-sm font-semibold transition-colors ${tab === key ? "bg-[var(--leaf)] text-white" : "text-[var(--ink-soft)] hover:bg-[var(--leaf-soft)]"}`}>
              <Icon size={15} /> <span className="truncate">{t(label)}</span>
            </button>
          ))}
        </div>

        <div className="mt-5">
          {tab === "game" && <SortGame />}
          {tab === "quiz" && <ClimateQuiz />}
          {tab === "tips" && <ActionTips />}
        </div>
      </div>
    </div>
  );
}
